import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  Edit,
  Person,
  LocalHospital,
  CalendarToday,
  AccessTime,
  Description,
  MonitorHeart,
  MedicalServices,
  People,
  Delete,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getICURound,
  updateICURound,
  deleteICURound,
} from "../../../../api/icu";
import useFetchObjects from "../../../../api/useFetchObjects";

const RoundView = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch round data
  const {
    data: round,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["icu-round", id],
    queryFn: () => getICURound(id, token),
    enabled: !!id && !!token,
  });

  // Fetch doctors and nurses for editing
  const { data: doctorsData } = useFetchObjects(
    ["users-doctors"],
    "users",
    token,
    { enabled: isEditing && !!token }
  );

  const { data: nursesData } = useFetchObjects(
    ["users-nurses"],
    "users",
    token,
    { enabled: isEditing && !!token }
  );

  const [roundForm, setRoundForm] = useState({
    doctor: "",
    round_type: 1,
    round_date: "",
    round_time: "",
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
    temperature: "",
    blood_pressure_systolic: "",
    blood_pressure_diastolic: "",
    heart_rate: "",
    respiratory_rate: "",
    oxygen_saturation: "",
    nurses_present: [],
    nursing_notes: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedNurses, setSelectedNurses] = useState([]);

  // Initialize form when round loads
  useEffect(() => {
    if (round && !isEditing) {
      const roundDate = new Date(round.round_date);
      setRoundForm({
        doctor: round.doctor || "",
        round_type: round.round_type || 1,
        round_date: roundDate.toISOString().split("T")[0],
        round_time: roundDate.toTimeString().slice(0, 5),
        subjective: round.subjective || "",
        objective: round.objective || "",
        assessment: round.assessment || "",
        plan: round.plan || "",
        temperature: round.temperature || "",
        blood_pressure_systolic: round.blood_pressure_systolic || "",
        blood_pressure_diastolic: round.blood_pressure_diastolic || "",
        heart_rate: round.heart_rate || "",
        respiratory_rate: round.respiratory_rate || "",
        oxygen_saturation: round.oxygen_saturation || "",
        nurses_present: round.nurses_present || [],
        nursing_notes: round.nursing_notes || "",
      });
      // Set selected nurses
      if (round.nurses_present && Array.isArray(round.nurses_present)) {
        setSelectedNurses(round.nurses_present);
      }
    }
  }, [round, isEditing]);

  const updateRoundMutation = useMutation({
    mutationFn: ({ id, data }) => updateICURound(id, data, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["icu-round", id]);
      queryClient.invalidateQueries(["icu-rounds-dashboard"]);
      queryClient.invalidateQueries(["icu-rounds"]);
      setIsEditing(false);
    },
    onError: (error) => {
      console.error("Error updating ICU round:", error);
      setFormErrors({
        submit:
          error.response?.data?.detail ||
          t("common.error_updating", "Error updating round."),
      });
    },
  });

  const deleteRoundMutation = useMutation({
    mutationFn: () => deleteICURound(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["icu-rounds-dashboard"]);
      queryClient.invalidateQueries(["icu-rounds"]);
      navigate("/icu/rounds");
    },
    onError: (error) => {
      console.error("Error deleting ICU round:", error);
      setFormErrors({
        submit:
          error.response?.data?.detail ||
          t("common.error_deleting", "Error deleting round."),
      });
    },
  });

  const filteredDoctors = Array.isArray(doctorsData)
    ? doctorsData.filter((d) => d.is_doctor || d.role === "doctor")
    : (doctorsData?.results || []).filter(
        (d) => d.is_doctor || d.role === "doctor"
      ) || [];

  const filteredNurses = Array.isArray(nursesData)
    ? nursesData.filter((n) => n.nurse === true || n.is_admin === true || n.is_nurse || n.role === "nurse")
    : (nursesData?.results || []).filter(
        (n) => n.nurse === true || n.is_admin === true || n.is_nurse || n.role === "nurse"
      ) || [];

  const validateForm = () => {
    const newErrors = {};
    if (!roundForm.subjective)
      newErrors.subjective = t("icu.rounds.errors.subjective_required");
    if (!roundForm.objective)
      newErrors.objective = t("icu.rounds.errors.objective_required");
    if (!roundForm.assessment)
      newErrors.assessment = t("icu.rounds.errors.assessment_required");
    if (!roundForm.plan) newErrors.plan = t("icu.rounds.errors.plan_required");
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const roundDateTime = new Date(
        `${roundForm.round_date}T${roundForm.round_time}`
      );

      const submitData = {
        ...roundForm,
        nurses_present: selectedNurses.map((nurse) => nurse.id),
        round_date: roundDateTime.toISOString(),
        status: 3, // Mark as completed when saved
      };
      updateRoundMutation.mutate({ id, data: submitData });
    }
  };

  const handleDelete = () => {
    deleteRoundMutation.mutate();
  };

  const getRoundTypeText = (type) => {
    switch (type) {
      case 1:
        return t("icu.rounds.morning", "Morning Round");
      case 2:
        return t("icu.rounds.evening", "Evening Round");
      case 3:
        return t("icu.rounds.night", "Night Round");
      default:
        return t("icu.rounds.round", "Round");
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return t("icu.rounds.pending", "Pending");
      case 2:
        return t("icu.rounds.in_progress", "In Progress");
      case 3:
        return t("icu.rounds.completed", "Completed");
      case 4:
        return t("icu.rounds.skipped", "Skipped");
      default:
        return "-";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "warning";
      case 2:
        return "info";
      case 3:
        return "success";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !round) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t("icu.rounds.error_loading", "Error loading round")}
          {error && `: ${error.message}`}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/icu/rounds")}
          sx={{ mt: 2 }}
        >
          {t("common.back", "Back")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate("/icu/rounds")}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {t("icu.rounds.round_details", "ICU Round Details")}
          </Typography>
          <Chip
            label={getStatusText(round.status)}
            color={getStatusColor(round.status)}
            size="small"
          />
        </Box>
        <Box display="flex" gap={1}>
          {!isEditing && (
            <>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setIsEditing(true)}
              >
                {t("common.edit", "Edit")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t("common.delete", "Delete")}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {isEditing ? (
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!formErrors.doctor}>
                  <InputLabel>
                    {t("icu.rounds.doctor", "Doctor Performing Round")}
                  </InputLabel>
                  <Select
                    value={roundForm.doctor}
                    onChange={(e) => {
                      setRoundForm({ ...roundForm, doctor: e.target.value });
                      setFormErrors((prev) => ({ ...prev, doctor: "" }));
                    }}
                    required
                  >
                    {filteredDoctors.map((doctor) => (
                      <MenuItem key={doctor.id} value={doctor.id}>
                        {doctor.first_name} {doctor.last_name}
                        {doctor.username && ` (${doctor.username})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("icu.rounds.type", "Round Type")}</InputLabel>
                  <Select
                    value={roundForm.round_type}
                    onChange={(e) =>
                      setRoundForm({ ...roundForm, round_type: e.target.value })
                    }
                  >
                    <MenuItem value={1}>
                      {t("icu.rounds.type.morning", "Morning")}
                    </MenuItem>
                    <MenuItem value={2}>
                      {t("icu.rounds.type.evening", "Evening")}
                    </MenuItem>
                    <MenuItem value={3}>
                      {t("icu.rounds.type.night", "Night")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.date", "Date")}
                  type="date"
                  value={roundForm.round_date}
                  onChange={(e) =>
                    setRoundForm({ ...roundForm, round_date: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.time", "Time")}
                  type="time"
                  value={roundForm.round_time}
                  onChange={(e) =>
                    setRoundForm({ ...roundForm, round_time: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t("icu.rounds.soap_notes", "SOAP Notes")}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.subjective", "Subjective")}
                  multiline
                  rows={3}
                  value={roundForm.subjective}
                  onChange={(e) => {
                    setRoundForm({ ...roundForm, subjective: e.target.value });
                    setFormErrors((prev) => ({ ...prev, subjective: "" }));
                  }}
                  error={!!formErrors.subjective}
                  helperText={formErrors.subjective}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.objective", "Objective")}
                  multiline
                  rows={3}
                  value={roundForm.objective}
                  onChange={(e) => {
                    setRoundForm({ ...roundForm, objective: e.target.value });
                    setFormErrors((prev) => ({ ...prev, objective: "" }));
                  }}
                  error={!!formErrors.objective}
                  helperText={formErrors.objective}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.assessment", "Assessment")}
                  multiline
                  rows={3}
                  value={roundForm.assessment}
                  onChange={(e) => {
                    setRoundForm({ ...roundForm, assessment: e.target.value });
                    setFormErrors((prev) => ({ ...prev, assessment: "" }));
                  }}
                  error={!!formErrors.assessment}
                  helperText={formErrors.assessment}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.plan", "Plan")}
                  multiline
                  rows={3}
                  value={roundForm.plan}
                  onChange={(e) => {
                    setRoundForm({ ...roundForm, plan: e.target.value });
                    setFormErrors((prev) => ({ ...prev, plan: "" }));
                  }}
                  error={!!formErrors.plan}
                  helperText={formErrors.plan}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t("icu.rounds.vital_signs", "Vital Signs")}
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.temperature", "Temperature")}
                  type="number"
                  value={roundForm.temperature}
                  onChange={(e) =>
                    setRoundForm({ ...roundForm, temperature: e.target.value })
                  }
                  InputProps={{ endAdornment: "°C" }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.blood_pressure_systolic", "BP Systolic")}
                  type="number"
                  value={roundForm.blood_pressure_systolic}
                  onChange={(e) =>
                    setRoundForm({
                      ...roundForm,
                      blood_pressure_systolic: e.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t(
                    "icu.rounds.blood_pressure_diastolic",
                    "BP Diastolic"
                  )}
                  type="number"
                  value={roundForm.blood_pressure_diastolic}
                  onChange={(e) =>
                    setRoundForm({
                      ...roundForm,
                      blood_pressure_diastolic: e.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.heart_rate", "Heart Rate")}
                  type="number"
                  value={roundForm.heart_rate}
                  onChange={(e) =>
                    setRoundForm({ ...roundForm, heart_rate: e.target.value })
                  }
                  InputProps={{ endAdornment: "bpm" }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.respiratory_rate", "Respiratory Rate")}
                  type="number"
                  value={roundForm.respiratory_rate}
                  onChange={(e) =>
                    setRoundForm({
                      ...roundForm,
                      respiratory_rate: e.target.value,
                    })
                  }
                  InputProps={{ endAdornment: "/min" }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.oxygen_saturation", "Oxygen Saturation")}
                  type="number"
                  value={roundForm.oxygen_saturation}
                  onChange={(e) =>
                    setRoundForm({
                      ...roundForm,
                      oxygen_saturation: e.target.value,
                    })
                  }
                  InputProps={{ endAdornment: "%" }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t("icu.rounds.nurses_present", "Nurses Present")}
                </Typography>
                <Autocomplete
                  multiple
                  options={filteredNurses}
                  getOptionLabel={(option) =>
                    `${option.first_name || ""} ${
                      option.last_name || ""
                    }`.trim() ||
                    option.username ||
                    ""
                  }
                  value={selectedNurses}
                  onChange={(event, newValue) => {
                    setSelectedNurses(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("icu.rounds.select_nurses", "Select Nurses")}
                      placeholder={t(
                        "icu.rounds.nurses_placeholder",
                        "Select nurses present during this round"
                      )}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={`${option.first_name || ""} ${
                          option.last_name || ""
                        }`}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("icu.rounds.nursing_notes", "Nursing Notes")}
                  multiline
                  rows={3}
                  value={roundForm.nursing_notes}
                  onChange={(e) =>
                    setRoundForm({
                      ...roundForm,
                      nursing_notes: e.target.value,
                    })
                  }
                />
              </Grid>

              {formErrors.submit && (
                <Grid item xs={12}>
                  <Alert severity="error">{formErrors.submit}</Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<Save />}
                    disabled={updateRoundMutation.isPending}
                  >
                    {updateRoundMutation.isPending
                      ? t("common.saving", "Saving...")
                      : t("common.save", "Save")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsEditing(false);
                      setFormErrors({});
                      // Reset form to original round data
                      if (round) {
                        const roundDate = new Date(round.round_date);
                        setRoundForm({
                          doctor: round.doctor || "",
                          round_type: round.round_type || 1,
                          round_date: roundDate.toISOString().split("T")[0],
                          round_time: roundDate.toTimeString().slice(0, 5),
                          subjective: round.subjective || "",
                          objective: round.objective || "",
                          assessment: round.assessment || "",
                          plan: round.plan || "",
                          temperature: round.temperature || "",
                          blood_pressure_systolic:
                            round.blood_pressure_systolic || "",
                          blood_pressure_diastolic:
                            round.blood_pressure_diastolic || "",
                          heart_rate: round.heart_rate || "",
                          respiratory_rate: round.respiratory_rate || "",
                          oxygen_saturation: round.oxygen_saturation || "",
                          nurses_present: round.nurses_present || [],
                          nursing_notes: round.nursing_notes || "",
                        });
                        if (
                          round.nurses_present &&
                          Array.isArray(round.nurses_present)
                        ) {
                          setSelectedNurses(round.nurses_present);
                        }
                      }
                    }}
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Person color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {t("icu.rounds.patient", "Patient")}
                </Typography>
              </Box>
              <Typography variant="h6">
                {round.patient_name || round.admission?.patient_name || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("icu.rounds.admission", "Admission")} #
                {round.admission || round.admission_id || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LocalHospital color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {t("icu.rounds.doctor", "Doctor")}
                </Typography>
              </Box>
              <Typography variant="h6">{round.doctor_name || "-"}</Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CalendarToday color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {t("icu.rounds.date_time", "Date & Time")}
                </Typography>
              </Box>
              <Typography variant="body1">
                {round.round_date
                  ? new Date(round.round_date).toLocaleString()
                  : "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <MedicalServices color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {t("icu.rounds.type", "Round Type")}
                </Typography>
              </Box>
              <Typography variant="body1">
                {getRoundTypeText(round.round_type)}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t("icu.rounds.soap_notes", "SOAP Notes")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("icu.rounds.subjective", "Subjective")}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  {round.subjective || "-"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("icu.rounds.objective", "Objective")}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  {round.objective || "-"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("icu.rounds.assessment", "Assessment")}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  {round.assessment || "-"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("icu.rounds.plan", "Plan")}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">{round.plan || "-"}</Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t("icu.rounds.vital_signs", "Vital Signs")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {t("icu.rounds.temperature", "Temperature")}
                </Typography>
                <Typography variant="h6">
                  {round.temperature ? `${round.temperature}°C` : "-"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {t("icu.rounds.blood_pressure", "Blood Pressure")}
                </Typography>
                <Typography variant="h6">
                  {round.blood_pressure_systolic &&
                  round.blood_pressure_diastolic
                    ? `${round.blood_pressure_systolic}/${round.blood_pressure_diastolic}`
                    : "-"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {t("icu.rounds.heart_rate", "Heart Rate")}
                </Typography>
                <Typography variant="h6">
                  {round.heart_rate ? `${round.heart_rate} bpm` : "-"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {t("icu.rounds.respiratory_rate", "Respiratory Rate")}
                </Typography>
                <Typography variant="h6">
                  {round.respiratory_rate
                    ? `${round.respiratory_rate} /min`
                    : "-"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {t("icu.rounds.oxygen_saturation", "Oxygen Saturation")}
                </Typography>
                <Typography variant="h6">
                  {round.oxygen_saturation
                    ? `${round.oxygen_saturation}%`
                    : "-"}
                </Typography>
              </Paper>
            </Grid>

            {round.nurses_present_names &&
              round.nurses_present_names.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t("icu.rounds.nurses_present", "Nurses Present")}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {round.nurses_present_names.map((name, index) => (
                      <Chip
                        key={index}
                        label={name}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

            {round.nursing_notes && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t("icu.rounds.nursing_notes", "Nursing Notes")}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">{round.nursing_notes}</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {t("icu.rounds.delete_confirm_title", "Delete Round?")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "icu.rounds.delete_confirm_message",
              "Are you sure you want to delete this round? This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteRoundMutation.isPending}
          >
            {deleteRoundMutation.isPending
              ? t("common.deleting", "Deleting...")
              : t("common.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoundView;
